/**
 * @file Lease lifecycle and lease input schemas (T1.2).
 *
 * Three things live here, and the separation is the point:
 *
 *   1. **The §6.2 states as an enum-shaped list**, so the database enum, the
 *      transition map and the SPA's status chips are the same eight words.
 *   2. **The transition map as DATA** — a record from a state to the states it may
 *      move to. PLAN.md §6 says the map lives in `packages/shared` and the service
 *      layer enforces it; writing it as a chain of `if`s would put the machine in
 *      a route where nothing can enumerate it, and where a new state is a silent
 *      omission rather than a compile error (`Record<LeaseStatus, …>` is total).
 *   3. **The successor-lease guard** (`leaseStatusAtEndOfTerm`), a pure decision
 *      function for the subtlest rule in the product. It reads facts, not rows, so
 *      it is testable without a database — see its own doc comment.
 *
 * Money is INTEGER CENTS everywhere (CLAUDE.md / INV-MONEY-1). No schema here
 * accepts dollars, decimals or floats for an amount.
 *
 * The SPA imports the TYPES from here and (for the status list) the label map;
 * the API is the authority on what is valid, and a browser bundle shipping its
 * own copy of these validation rules would be a second, drifting authority.
 */
import { z } from "zod";

/**
 * The lease lifecycle states, in the order PLAN.md §6.2 draws them.
 *
 * This list is mirrored by the `lease_status` enum in
 * `packages/db/migrations/0005_leases.sql`; a state added in one place and not
 * the other is caught by the enum refusing the value at write time.
 */
export const LEASE_STATUSES = [
  "draft",
  "active",
  "month_to_month",
  "renewing",
  "terminating",
  "ended",
  "deposit_reconciliation",
  "closed",
] as const;

export type LeaseStatus = (typeof LEASE_STATUSES)[number];

/** Mutable-tuple form, for `z.enum` (mirrors PROPERTY_TYPE_VALUES). */
export const LEASE_STATUS_VALUES = LEASE_STATUSES.map(
  (status) => status,
) as [LeaseStatus, ...LeaseStatus[]];

/**
 * Human labels for the same eight states.
 *
 * Here rather than in the SPA because two screens will read them (the lease list
 * today, the lease detail later), and "month_to_month" appearing raw in a badge
 * is how a status list starts disagreeing with itself.
 */
export const LEASE_STATUS_LABELS: Readonly<Record<LeaseStatus, string>> = {
  draft: "Draft",
  active: "Active",
  month_to_month: "Month to month",
  renewing: "Renewing",
  terminating: "Terminating",
  ended: "Ended",
  deposit_reconciliation: "Deposit reconciliation",
  closed: "Closed",
};

/** How the rent is owed. Mirrored by the `lease_mode` enum (0005). */
export const LEASE_MODES = ["joint", "split"] as const;
export type LeaseMode = (typeof LEASE_MODES)[number];
export const LEASE_MODE_VALUES = LEASE_MODES.map(
  (mode) => mode,
) as [LeaseMode, ...LeaseMode[]];

/** How one tenant's share of a split lease is expressed. Mirrored by 0005. */
export const LEASE_SHARE_TYPES = ["equal", "fixed", "percent"] as const;
export type LeaseShareType = (typeof LEASE_SHARE_TYPES)[number];
export const LEASE_SHARE_TYPE_VALUES = LEASE_SHARE_TYPES.map(
  (share) => share,
) as [LeaseShareType, ...LeaseShareType[]];

/**
 * The §6.2 transition map, as data.
 *
 * Every state has an entry — `Record<LeaseStatus, …>` is total, so adding a state
 * without saying where it can go fails to compile rather than silently becoming
 * unreachable. The edges are exactly the ones §6.2 draws:
 *
 *   draft → active → month_to_month → terminating → ended →
 *   deposit_reconciliation → closed,  with active ↔ renewing
 *
 * plus `active → ended`, which §6.2's successor guard names explicitly as the
 * other outcome of end-date passage.
 *
 * Deliberately NOT here: any edge the plan does not draw. `active → terminating`
 * is the tempting one — a real lease can be terminated mid-term — but §6.2 puts
 * `terminating` after `month_to_month`, and adding edges of our own is inventing
 * a state machine rather than implementing the specified one. When a workflow
 * needs it, that is a plan change, not a local one.
 */
export const LEASE_TRANSITIONS: Readonly<Record<LeaseStatus, readonly LeaseStatus[]>> = {
  // A draft is not yet an agreement; activation is the first real move.
  draft: ["active"],
  // `month_to_month` and `ended` are BOTH legal from active — which one a given
  // lease takes at its end date is the successor guard's decision below.
  active: ["month_to_month", "renewing", "ended"],
  month_to_month: ["terminating"],
  // §6.2: "active ↔ renewing". A renewal spawns a linked new draft; history is
  // never overwritten, so the old lease comes back to active only if that
  // renewal is abandoned.
  renewing: ["active"],
  terminating: ["ended"],
  // The deposit cannot be reconciled before the tenancy has ended.
  ended: ["deposit_reconciliation"],
  deposit_reconciliation: ["closed"],
  // Terminal. Nothing follows a closed lease; a new term is a new lease.
  closed: [],
};

/** May a lease move from `from` to `to`? The §6.2 map, as one question. */
export function canTransitionLease(from: LeaseStatus, to: LeaseStatus): boolean {
  return LEASE_TRANSITIONS[from].includes(to);
}

/** Every state a lease in `from` may move to — for a UI or an error message. */
export function nextLeaseStatuses(from: LeaseStatus): readonly LeaseStatus[] {
  return LEASE_TRANSITIONS[from];
}

/**
 * The facts end-date passage depends on. Facts, not rows: this function never
 * touches a database, which is what makes the trickiest rule in the product
 * testable in a hermetic unit test.
 */
export interface LeaseEndOfTermFacts {
  /**
   * Does a successor lease already exist on this unit — a renewal that is a
   * draft, active, or anything else that is not closed?
   */
  readonly successorLeaseExists: boolean;
  /** Has a termination been scheduled (notice given, an end date agreed)? */
  readonly terminationScheduled: boolean;
}

/**
 * **The successor-lease guard** (PLAN.md §6.2, V7.1 §G.1).
 *
 * `active → month_to_month` fires on end-date passage ONLY if no successor lease
 * exists on the unit AND no termination is scheduled; otherwise `active → ended`.
 *
 * Why it matters: a fixed term that simply runs out rolls into a month-to-month
 * tenancy — the landlord keeps collecting rent, nothing needs signing. But that
 * is only true while the unit's future is undecided. If the landlord has already
 * signed the next tenant, the old lease has genuinely ENDED: rolling it into
 * month-to-month would keep a tenancy alive on a unit that is about to be
 * occupied by someone else, and two active leases on one unit is exactly the
 * state this guard exists to prevent. A scheduled termination is the same
 * situation by the other route — the end was decided, so the lease ended.
 *
 * Both inputs default to "undecided", so the honest answer when the caller knows
 * nothing is month-to-month — the ordinary, no-decision outcome.
 */
export function leaseStatusAtEndOfTerm(
  facts: LeaseEndOfTermFacts,
): "month_to_month" | "ended" {
  return facts.successorLeaseExists || facts.terminationScheduled
    ? "ended"
    : "month_to_month";
}

/**
 * `status_transitions.entity_type` for a lease.
 *
 * One spelling, exported, so the writer and the test that reads the audit row
 * cannot disagree about it.
 */
export const LEASE_ENTITY_TYPE = "lease";

// ---------------------------------------------------------------------------
// Input schemas
// ---------------------------------------------------------------------------

/** Required, trimmed, length-bounded text. */
function requiredText(max: number) {
  return z.string().trim().min(1, "is required").max(max);
}

/**
 * Optional text stored as `null` when absent or blank — the same normalisation
 * `packages/shared/src/properties.ts` uses, and for the same reason: a cleared
 * form field must not persist as `""`, or "no phone number" gets two
 * representations and every later query has to remember both.
 */
function optionalText(max: number) {
  return z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((value) =>
      value === undefined || value === null || value.length === 0 ? null : value,
    );
}

/**
 * Optional email, normalised to `null` when blank.
 *
 * The blank case is allowed BEFORE the format check, not after: a `.email()`
 * first would reject `""` with "must be an email address", so a landlord clearing
 * the field would be told they typed something wrong. Same trick, same reason, as
 * the date fields below.
 */
function optionalEmail(max = 254) {
  return z
    .string()
    .trim()
    .max(max)
    .refine(
      (value) => value === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
      "must be an email address",
    )
    .nullish()
    .transform((value) =>
      value === undefined || value === null || value.length === 0 ? null : value,
    );
}

/** `YYYY-MM-DD`, the only date format this API accepts. */
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const DATE_HINT = "must be a YYYY-MM-DD date";

/**
 * A required date.
 *
 * `refine` rather than `.regex()` so the failure message is ours and the check
 * reads as one rule — the pattern is an implementation detail of "a date".
 */
function requiredDate() {
  return z.string().trim().refine((value) => DATE_PATTERN.test(value), DATE_HINT);
}

/**
 * An optional date, blank → `null`.
 *
 * The empty string is allowed BEFORE the format check (documented in
 * `properties.ts`): `.regex()` first would reject `""` with a format error, so a
 * caller clearing `move_out` would get "must be a YYYY-MM-DD date" for leaving it
 * empty.
 */
function optionalDate() {
  return z
    .string()
    .trim()
    .refine((value) => value === "" || DATE_PATTERN.test(value), DATE_HINT)
    .nullish()
    .transform((value) =>
      value === undefined || value === null || value.length === 0 ? null : value,
    );
}

/**
 * Money in integer cents, never negative (CLAUDE.md: no floats).
 *
 * `.int()` is not cosmetic: `1850.50` as a dollar amount would be a number the
 * column cannot store, and a float that reaches Postgres' `integer` is an error
 * at write time rather than a message at the boundary.
 */
const moneyCents = z
  .number()
  .int("money must be whole cents — no decimals")
  .min(0, "cannot be negative");

/**
 * One person on a lease.
 *
 * `userId` is deliberately NOT accepted: it is NULL until the invite is accepted
 * and a real user exists (T1.7), and letting a request body name a user id would
 * be a way to attach an arbitrary account to a lease.
 *
 * `guarantorContactId` is likewise absent, and still is after T2.9 gave it a real
 * composite foreign key (migration 0011): the tenant↔guarantor relation is
 * writable from the GUARANTOR side (`guarantor_contacts.lease_tenant_id`, through
 * the Guarantors pages), and no tenant-side body accepts this id.
 */
export const leaseTenantCreateSchema = z.object({
  fullName: requiredText(120),
  email: optionalEmail(),
  phone: optionalText(40),
  /**
   * `equal` by default: on a joint lease every tenant's share IS equal, and it is
   * the shape PLAN.md §9's descope ladder treats as covering most beta houses.
   * A split lease says so explicitly.
   */
  shareType: z.enum(LEASE_SHARE_TYPE_VALUES).default("equal"),
  /**
   * Integer by necessity, not convenience: integer CENTS when `shareType` is
   * `fixed` (money — no floats), a whole percent when it is `percent` (see the
   * column comment in 0005), NULL when `equal`.
   */
  shareValue: z
    .number()
    .int("must be a whole number — cents for a fixed share, whole percent otherwise")
    .min(0, "cannot be negative")
    .nullish(),
  moveIn: optionalDate(),
  moveOut: optionalDate(),
});

/**
 * POST /workspaces/:workspaceId/leases body.
 *
 * A lease AND its tenants in one request, for the reason the property form takes
 * its units: a 4-tenant house is one agreement, and N follow-up POSTs would be N
 * transactions with a half-written lease if connectivity dropped in the middle.
 *
 * NOT accepted here, each for a reason:
 *   - `status` — the §6.2 machine owns it; a client that could set it could skip
 *     the transition route and its audit row entirely.
 *   - `lateFee` — founder-owned policy, left as the empty JSONB default.
 *   - `predecessorLeaseId` — renewals spawn a linked draft through a renewal flow
 *     that does not exist yet; accepting the id now would let a caller assert a
 *     history nobody verified.
 *   - `depositAmountCents` is optional and defaults to 0, which is a real state
 *     (no deposit taken), not an invented one.
 *
 * **`pdfFileId` IS accepted, and this is a reversal (T0.8 Phase B).** This schema
 * used to omit it, and the omission was right while there was no upload route and no
 * file UI in the repo: an accepted id would have named a document nobody could open.
 * Both halves now exist — `POST /workspaces/:id/documents` stores one with the
 * `lease` domain, `GET …/documents/:documentId` serves it back and audits the read,
 * and `AttachmentField` is the picker — so the column `leases.pdf_file_id` (0005)
 * gets its writer. The id is verified by the ROUTE against `documents` in this
 * workspace with the `lease` domain (`apps/api/src/domain/documents.ts`); zod cannot
 * ask that question because it is about rows.
 *
 * `null` (or an absent field) means "no signed agreement on file", which is a real
 * state for a lease that predates this feature. A caller CLEARS the link by sending
 * `pdfFileId: null`; leaving the key out of a PATCH leaves the column alone.
 */
export const leaseCreateSchema = z.object({
  unitId: z.string().uuid("must be a unit id"),
  startDate: requiredDate(),
  endDate: requiredDate(),
  rentAmountCents: moneyCents,
  /**
   * Day of the month rent falls due. Bounds are data-shape guards that mirror the
   * CHECK in 0005 — not a policy about when rent may be due.
   */
  billingDay: z.number().int().min(1).max(31).default(1),
  /** Days after the due date before a payment is late. No late-fee rule here. */
  graceDays: z.number().int().min(0).max(365).default(5),
  depositAmountCents: moneyCents.default(0),
  mode: z.enum(LEASE_MODE_VALUES),
  /**
   * The signed agreement, as a `documents` id in the `lease` domain.
   *
   * Optional and null by default: a lease with no scan on file is the ordinary
   * state, and requiring one here would refuse to record an agreement that already
   * exists on paper. `.nullish()` with no coercion, because a blank string is NOT a
   * document id — a client that wants to clear the link sends `null`, and a client
   * that sends `""` gets a validation error naming the field rather than a silent
   * clear.
   */
  pdfFileId: z.string().uuid("must be a document id").nullish().transform((v) => v ?? null),
  tenants: z
    .array(leaseTenantCreateSchema)
    .max(20, "that is a lot of tenants")
    .default([]),
});

/**
 * PATCH body: `partial()` on the CREATE schema, so a field added to create is
 * updatable by default and cannot be forgotten (same reasoning as
 * `propertyUpdateSchema`).
 *
 * Two omissions are deliberate. `unitId` — moving an existing lease to another
 * unit is a different operation with its own consequences (deposit, charges,
 * occupants), not a field edit. `tenants` — a tenant list is replaced through the
 * tenant routes, not by sending a whole new array to a PATCH that would silently
 * delete anyone omitted.
 */
export const leaseUpdateSchema = leaseCreateSchema
  .omit({ unitId: true, tenants: true })
  .partial();

/**
 * PATCH /workspaces/:workspaceId/tenants/:tenantId body — ONE person's contact and
 * share details, and nothing else.
 *
 * `.partial()` on the tenant create schema, so a field added when a tenant is first
 * recorded is editable by default and cannot be forgotten — the rule
 * `leaseUpdateSchema` states above its own omissions. A field the caller leaves out
 * is not written; `email: null` clears it.
 *
 * **`leaseId` and `userId` are unrepresentable here.** This is `.partial()` on the
 * tenant create shape rather than a second object literal, so the fields a person may
 * edit are definitionally the fields that create them — a field added to create is
 * editable here without anyone remembering to add it. Neither `leaseId` nor `userId`
 * is in that shape, and the doc comment names them because they are the two a later
 * widening would most plausibly reach for:
 *
 *   - **`leaseId`** — moving a person between agreements (or onto one) is a
 *     different operation with its own consequences: the charges a lease carries,
 *     which unit is occupied, and the balance a payment attributed to them is folded
 *     against. It is not a field edit, exactly as `unitId` is not one on a lease.
 *     `leaseTenantCreateSchema` does not accept it either.
 *   - **`userId`** — the portal invite (T1.7) is what sets it, when an invite is
 *     accepted and a real user exists. A request body that could name a user id
 *     would be a way to attach an arbitrary account to a lease.
 *
 * The route additionally REFUSES a body that names either field, in both spellings,
 * with `tenant_link_not_editable_here` — a stripped key would leave the row alone,
 * but a caller who sent one and got a 200 back would reasonably believe it had
 * changed something.
 */
export const leaseTenantUpdateSchema = leaseTenantCreateSchema.partial();

/** POST /workspaces/:workspaceId/leases/:leaseId/transitions body. */
export const leaseTransitionSchema = z.object({
  to: z.enum(LEASE_STATUS_VALUES),
});

export type LeaseCreateInput = z.infer<typeof leaseCreateSchema>;
export type LeaseUpdateInput = z.infer<typeof leaseUpdateSchema>;
export type LeaseTenantCreateInput = z.infer<typeof leaseTenantCreateSchema>;
export type LeaseTenantUpdateInput = z.infer<typeof leaseTenantUpdateSchema>;
export type LeaseTransitionInput = z.infer<typeof leaseTransitionSchema>;

/** The PRE-parse shapes, for the SPA to build request bodies from. */
export type LeaseCreateBody = z.input<typeof leaseCreateSchema>;
export type LeaseUpdateBody = z.input<typeof leaseUpdateSchema>;
export type LeaseTenantCreateBody = z.input<typeof leaseTenantCreateSchema>;
export type LeaseTenantUpdateBody = z.input<typeof leaseTenantUpdateSchema>;
export type LeaseTransitionBody = z.input<typeof leaseTransitionSchema>;

/**
 * The compile-time statement that a tenant PATCH cannot carry `leaseId` or `userId`.
 *
 * `leaseTenantUpdateSchema` refuses both at runtime (zod strips an unknown key, so the
 * parsed value never has one, and the route refuses a body that NAMES one with a
 * sentence rather than answering 200 to a no-op). The route pins this type beside its
 * `UPDATE … SET`:
 *
 * ```ts
 * const _neverCarriesLink: LeaseTenantUpdateNeverCarriesLink = true;
 * ```
 *
 * so a later edit that widened the update schema to accept `leaseId` or `userId` —
 * which would silently move a person between agreements, or attach an arbitrary
 * account to a lease — fails to COMPILE rather than shipping. `true` only typechecks
 * while the update shape genuinely excludes both.
 */
export type LeaseTenantUpdateNeverCarriesLink =
  LeaseTenantUpdateInput extends { readonly leaseId?: unknown }
    ? false
    : LeaseTenantUpdateInput extends { readonly userId?: unknown }
      ? false
      : true;

// ---------------------------------------------------------------------------
// DTOs — what the API returns
// ---------------------------------------------------------------------------

/**
 * One person on a lease, as the API returns them.
 *
 * Declared here rather than inferred from the drizzle schema so the kernel does
 * not depend on a database package (dependency direction: apps → packages).
 */
export interface LeaseTenantDto {
  readonly id: string;
  readonly leaseId: string;
  /** NULL until the invite is accepted (T1.7). */
  readonly userId: string | null;
  readonly fullName: string;
  readonly email: string | null;
  readonly phone: string | null;
  readonly shareType: LeaseShareType;
  /** Integer CENTS for a fixed share, whole percent for a percent share. */
  readonly shareValue: number | null;
  readonly moveIn: string | null;
  readonly moveOut: string | null;
  /**
   * The guarantor this person named, if any. Composite-FK'd to
   * `guarantor_contacts` ON DELETE RESTRICT since migration 0011 (T2.9), and still
   * **written by nothing**: the relation is writable from the OTHER end today
   * (`guarantor_contacts.lease_tenant_id`, via the Guarantors page), and no
   * tenant-side route accepts this id.
   */
  readonly guarantorContactId: string | null;
}

/**
 * A lease, as the API returns it.
 *
 * Every money field is integer cents. `lateFee` is the rule object and stays
 * empty until a founder decides what a late fee is.
 */
export interface LeaseDto {
  readonly id: string;
  readonly unitId: string;
  readonly status: LeaseStatus;
  readonly startDate: string;
  readonly endDate: string;
  readonly rentAmountCents: number;
  readonly billingDay: number;
  readonly graceDays: number;
  readonly lateFee: Readonly<Record<string, unknown>>;
  readonly depositAmountCents: number;
  readonly mode: LeaseMode;
  readonly predecessorLeaseId: string | null;
  /**
   * The signed agreement on file, or NULL. A `documents` row of the `lease` domain,
   * verified by the route before it is written (T0.8 Phase B); the bytes are fetched
   * through `GET …/documents/:documentId`, which writes the audit row.
   */
  readonly pdfFileId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** A lease with its people, for the detail screen (one round trip). */
export interface LeaseWithTenantsDto extends LeaseDto {
  readonly tenants: readonly LeaseTenantDto[];
}

/**
 * A lease plus its unit and property, for the portfolio-wide list.
 *
 * The Leases screen answers "what is rented out, to whom, on what terms" across
 * every building, so each row carries enough context to be identifiable without a
 * request per row. `propertyLabel` is nullable because a property can have no
 * label; the address fields are the fallback the screen renders.
 */
export interface LeaseListRowDto extends LeaseWithTenantsDto {
  readonly unitLabel: string;
  readonly propertyId: string;
  readonly propertyLabel: string | null;
  readonly addressLine1: string;
  readonly city: string;
  readonly state: string;
}

/**
 * One person on a lease plus where they live, for the portfolio-wide Tenants
 * screen.
 *
 * The inverse of `LeaseListRowDto`: that one starts from the AGREEMENT and nests
 * the people, this one starts from the PERSON and names the agreement. Both are
 * needed because a landlord asks both questions — "who is on this lease" when
 * signing, and "who is this, and where do they live" when a name appears on a
 * payment or a text message.
 *
 * Deliberately absent: any money. What a person has been charged or has paid
 * needs `rent_charges` and the ledger (T1.3+), so a balance field here would be
 * an invented number on a screen whose whole job is identifying a human.
 */
export interface TenantListRowDto {
  readonly id: string;
  readonly fullName: string;
  readonly email: string | null;
  readonly phone: string | null;
  /** NULL until the invite is accepted (T1.7) — a real state, not an omission. */
  readonly userId: string | null;
  readonly shareType: LeaseShareType;
  /** Integer CENTS for a fixed share, whole percent for a percent share. */
  readonly shareValue: number | null;
  readonly moveIn: string | null;
  readonly moveOut: string | null;
  readonly leaseId: string;
  readonly leaseStatus: LeaseStatus;
  readonly unitId: string;
  readonly unitLabel: string;
  readonly propertyId: string;
  readonly propertyLabel: string | null;
  readonly addressLine1: string;
  readonly city: string;
  readonly state: string;
}

/**
 * GET (and PATCH) `/workspaces/:workspaceId/tenants/:tenantId` — ONE person, with
 * every hop of the chain their own page needs, in one payload.
 *
 * The tenant's own page is the mirror of `LeaseListRowDto`: that one starts from the
 * agreement and nests the people, `TenantListRowDto` starts from the person and names
 * the agreement, and this is the single person's own read — the same rows the list
 * returns, plus the unit, the property and its `timezone`, so the breadcrumb chain
 * (`Properties › 123 N Main St › Apt 1 › Ada Reyes`) renders on the first paint
 * instead of filling in after a second request. That is why the unit and property
 * fields are spread FLAT here rather than nested: the page renders one chain, and a
 * nested object would only add a level for the caller to walk.
 *
 * `guarantorContactId` is the tenant-side half of the T2.9 link and is returned
 * because the page needs the ID to link to the guarantor's own page. It is still
 * **written by nothing on this side** — the relation is writable from the guarantor's
 * `lease_tenant_id`, through the Guarantors pages — and `leaseTenantUpdateSchema`
 * does not accept it.
 *
 * **The balance fields are the per-tenant view, exactly as `GET /balances` computes
 * it.** `paidCents` is integer cents of payments the ledger attributes to this
 * person; `owesCents` is `null` with `owesReason` beside it, because an obligation is
 * owed by the LEASE and the platform does not invent a per-person split of it — on a
 * joint lease there is no split at all, and on a split lease the shares are the
 * landlord's arithmetic rather than a bill (`JOINT_LEASE_NO_ALLOCATION_REASON`,
 * `SPLIT_LEASE_NO_ALLOCATION_REASON`). Both are folded by the SAME
 * `perTenantPaymentTotals` the balances route calls, so the two surfaces cannot
 * disagree about a person's money.
 */
export interface TenantDetailDto {
  readonly id: string;
  readonly fullName: string;
  readonly email: string | null;
  readonly phone: string | null;
  /** NULL until the invite is accepted (T1.7) — a real state, not an omission. */
  readonly userId: string | null;
  readonly shareType: LeaseShareType;
  /** Integer CENTS for a fixed share, whole percent for a percent share. */
  readonly shareValue: number | null;
  readonly moveIn: string | null;
  readonly moveOut: string | null;
  /** The guarantor this person named, or NULL. Read-only on this side (T2.9). */
  readonly guarantorContactId: string | null;

  readonly leaseId: string;
  readonly leaseStatus: LeaseStatus;
  readonly startDate: string;
  readonly endDate: string;
  readonly rentAmountCents: number;
  readonly mode: LeaseMode;

  readonly unitId: string;
  readonly unitLabel: string;
  readonly propertyId: string;
  readonly propertyLabel: string | null;
  readonly addressLine1: string;
  readonly city: string;
  readonly state: string;
  /** The PROPERTY's IANA zone — the calendar a date on this page is read on. */
  readonly timezone: string;

  /** Integer cents of payments the ledger attributes to this person. */
  readonly paidCents: number;
  /** Always NULL in this build; read `owesReason` for why. */
  readonly owesCents: null;
  readonly owesReason: string;
}
